/**
 * The transcript shape the Copilot workspace renders, and the small helpers
 * shared by the pieces that render it.
 *
 * Extracted from CopilotPage so `MessageList`, `Composer` and `SourceList` can
 * import the type without importing the page.
 */
import type {
  CopilotAttribution,
  McpTurnSource,
  RetrievedSource,
} from "@yazamco/shared-types";

export interface ChatMessage {
  id: number;
  role: "user" | "assistant" | "error" | "stopped";
  text: string;
  /** True only while this answer is still receiving upstream snapshots. */
  streaming?: boolean;
  attributions?: CopilotAttribution[];
  /** Titles and locations for the cited links. */
  retrievedSources?: RetrievedSource[];
  /** The business systems this answer was given, named for the reader. */
  mcpSources?: McpTurnSource[];
  /** A refused question, kept so it can be asked across all of Microsoft 365. */
  retryUnscoped?: string;
  /**
   * The question this error interrupted, kept so it can simply be asked again.
   *
   * Distinct from `retryUnscoped`, which re-aims the question at all of
   * Microsoft 365 because the scope was the problem. Here nothing is changed —
   * a Graph call timed out, the upstream returned a 5xx — and the whole fix is
   * to try the same thing once more, which is the canvas's plain "Retry".
   */
  retryPrompt?: string;
  time?: string;
}

/** A document pinned to the next turn. Only `uri` is ever sent upstream. */
export interface PendingFile {
  uri: string;
  name?: string;
}

/**
 * The one place the per-turn attachment ceiling is stated. Matches the API's
 * own `files` limit in `copilotChatSchema`.
 */
export const MAX_FILES = 5;

export function messageTime(iso?: string): string {
  return new Date(iso ?? Date.now()).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function contextFileName(uri: string): string {
  const lastSegment = uri.split("/").filter(Boolean).at(-1) ?? uri;
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
}
